/*
 * SonarQube
 * Copyright (C) 2009-2025 SonarSource SA
 * mailto:info AT sonarsource DOT com
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 3 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program; if not, write to the Free Software Foundation,
 * Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 */

import {
  BranchBase,
  BranchLikeBase,
  BranchLikeParameters,
  BranchParameters,
  PullRequestBase,
} from '../types/branch-like';

export function getBranchLikeQuery<T extends BranchLikeBase>(
  branchLike?: T,
  withMainBranch = false,
): BranchParameters {
  if (isBranch(branchLike) && (withMainBranch || !isMainBranch(branchLike))) {
    return { branch: branchLike.name };
  } else if (isPullRequest(branchLike)) {
    return { pullRequest: branchLike.key };
  }
  return {};
}

export function getBranchLikeWithKeyQuery<T extends BranchLikeBase>(
  branchLike?: T,
  withMainBranch = false,
): BranchLikeParameters {
  if (isBranch(branchLike) && (withMainBranch || !isMainBranch(branchLike))) {
    return { branchKey: branchLike.name };
  } else if (isPullRequest(branchLike)) {
    return { pullRequestKey: branchLike.key };
  }
  return {};
}
export function isBranch<T extends BranchBase>(branchLike?: T | PullRequestBase): branchLike is T {
  return branchLike !== undefined && (branchLike as T).isMain !== undefined;
}

export function isMainBranch<T extends BranchBase>(
  branchLike?: T | PullRequestBase,
): branchLike is T & { isMain: true } {
  return isBranch(branchLike) && branchLike.isMain;
}

export function isPullRequest<T extends PullRequestBase>(
  branchLike?: T | BranchBase,
): branchLike is T {
  return branchLike !== undefined && (branchLike as T).key !== undefined;
}

/**
 * Get display name for branch-like objects using shared types.
 *
 * This function provides the same behavior as:
 * - libs/sq-server-commons/src/helpers/branch-like.ts:getBranchLikeDisplayName
 * - private/apps/sq-cloud/src/helpers/branch-like.ts:getBranchLikeDisplayName
 *
 * But uses only shared types (BranchLikeBase, BranchBase, PullRequestBase) instead of
 * product-specific types (BranchLike, Branch, PullRequest).
 *
 * @param branchLike - The branch-like object to get display name for
 * @returns Display name: branch name for branches, "key – title" for pull requests
 */
export function getBranchLikeDisplayName(branchLike: BranchLikeBase): string {
  return isPullRequest(branchLike) ? getPullRequestDisplayName(branchLike) : branchLike.name;
}

export function getPullRequestDisplayName(pullRequest: PullRequestBase) {
  return `${pullRequest.key} – ${pullRequest.title}`;
}
